import React from 'react'
import { IColumnValuesCount, ISelectedGroupCell, IViewPortColumn } from 'components/spreadsheet/types'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import { ATTACHMENT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import {
  IFile,
  ICellValue,
  ITableViewColour,
  IContextMenuState,
  ITableRow,
  IRowLevelColour,
  IJsonCellValue
} from 'types'
import { previewOf } from 'components/spreadsheet/helpers/jsonCell'
import { formatPercentDisplayValue, formatDate, formatDateTime } from 'components/spreadsheet/helpers/format'
import { getCellColour } from 'components/spreadsheet/helpers/colouring'
import { Link, Triangle } from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { groupCellStyles } from 'components/spreadsheet/components/cell/styles'
import { useThemedColours } from 'hooks/useTheme'

const styles = groupCellStyles()

const isRenderable = (value: ICellValue): boolean => {
  if (value === null || value === undefined || typeof value !== 'object') return true
  return Array.isArray(value) && value.every((item) => item === null || typeof item !== 'object')
}

const toDisplayString = (value: ICellValue): string => {
  if (!value) return ''
  // Arrays keep their existing comma-joined rendering; only bare objects need stringifying, and
  // they'd otherwise show as "[object Object]". previewOf is what the JSON cell itself renders, so
  // a group header reads the same as the cells under it.
  if (typeof value !== 'object' || Array.isArray(value)) return value.toString()
  return previewOf(value as unknown as IJsonCellValue)
}

interface Props {
  column: IViewPortColumn
  rowHeight: number
  colourSettings: ITableViewColour[]
  groupKey: string
  onGroupToggle: (groupKey: string) => void
  value: ICellValue
  canCollapse: boolean
  isWithinCollapsedGroup?: boolean
  onContext: (menu: IContextMenuState) => void
  shouldShowValue: boolean
  columnValuesCount: IColumnValuesCount
  spanWidth?: number // Width available for text wrapping (spans across subsequent group columns)
  rowSpanCount?: number // Number of rows this group label can span vertically
  row?: ITableRow | null
  rowLevelColour?: IRowLevelColour | null
  groupItemKey?: string
  inSelectedColumn?: boolean
  selected?: boolean
  onSelect?: (groupCell: ISelectedGroupCell) => void
  /** The grid's first row, which sits under the header's line and draws none of its own. */
  isFirstRow?: boolean
}

const GroupCell: React.FC<Props> = ({
  value,
  column,
  rowHeight,
  colourSettings,
  onGroupToggle,
  groupKey,
  canCollapse,
  isWithinCollapsedGroup,
  onContext,
  shouldShowValue,
  columnValuesCount,
  spanWidth,
  rowSpanCount = 1,
  row,
  rowLevelColour,
  groupItemKey,
  inSelectedColumn = false,
  selected = false,
  onSelect,
  isFirstRow = false
}) => {
  const themed = useThemedColours()
  const columnId = column.column.publicId

  const rowHeightValue = getRowHeightVariable(rowHeight)
  const heightPx = `${rowHeightValue}px`
  // Calculate the maximum height available for text wrapping (spans multiple rows)
  const spanHeightPx = rowHeightValue * rowSpanCount
  // Where a cell's text starts in a row: its 20px line, centred.
  const firstLineTop = Math.max(0, Math.floor((rowHeightValue - 20) / 2))
  const handleToggle = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    onGroupToggle(groupKey)
  }

  const handleOnMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !onSelect || !groupItemKey || !row?.publicId) return
    onSelect({ columnId, groupKey: groupItemKey, rowId: row.publicId })
  }

  const cellColour = getCellColour(
    column.column,
    columnValuesCount,
    row ?? null,
    value,
    colourSettings,
    true,
    undefined,
    rowLevelColour
  )

  // Use spanWidth if provided, otherwise fall back to column width
  // Subtract space for the collapse triangle button (40px)
  const maxWidth = (spanWidth ?? column.column.width) - 40

  // This is actually what gets displayed. Objects are not valid React children — rendering one throws
  // React error #31 and the grid's error boundary replaces the whole view (#10714) — so anything
  // object-shaped is reduced to text before it gets here.
  let displayStringValue = toDisplayString(value)
  let displayValue: React.ReactNode = isRenderable(value) ? (value as React.ReactNode) : displayStringValue

  if (value !== undefined && value !== null) {
    if (ATTACHMENT_COLUMN_TYPES.includes(column.column.kind) && Array.isArray(value)) {
      displayStringValue = (value as IFile[]).map((file) => file.filename).join(',')
      displayValue = displayStringValue
    } else if (column.column.kind === 'percentage') {
      displayStringValue = formatPercentDisplayValue(value as number)
      displayValue = displayStringValue
    } else if (column.column.kind === 'date' && column.column.dateFormat) {
      displayStringValue = formatDate(value as string, column.column.dateFormat)
      displayValue = displayStringValue
    } else if (column.column.kind === 'datetime' && column.column.dateFormat) {
      displayStringValue = formatDateTime(value as string, column.column.dateFormat)
      displayValue = displayStringValue
    } else if (column.column.kind === 'checkbox') {
      displayValue = <input type="checkbox" checked={value !== null && value === true} disabled={true} />
      displayStringValue = value !== null && value === true ? 'true' : 'false'
    } else if (column.column.kind === 'select') {
      displayStringValue = String(value)
      displayValue = (
        <span id="select-value-list" className="flex flex-wrap overflow-hidden p-0">
          <span
            id="select-value-item"
            data-testid="select-value-item"
            className="whitespace-pre-line break-words bg-surface-sunken rounded h-fit border border-divider text-body m-0.5 py-0 px-[3px]"
          >
            {displayStringValue}
          </span>
        </span>
      )
    } else if (column.column.kind === 'multiselect') {
      const stringValue = value && Array.isArray(value) ? (value as Array<string>) : []
      displayValue = (
        <span id="select-value-list" className="flex flex-wrap overflow-hidden p-0">
          {stringValue.map((item: string, itemNumber: number) => {
            const multiselectCellColour = getCellColour(
              column.column,
              columnValuesCount,
              row ?? null,
              item,
              colourSettings,
              true,
              undefined,
              rowLevelColour
            )

            return (
              <span
                key={itemNumber}
                id="select-value-item"
                data-testid="select-value-item"
                className="whitespace-pre-line break-words bg-surface-sunken rounded h-fit border border-divider m-0.5 py-0 px-[3px]"
                style={{
                  backgroundColor: column ? themed.fill(multiselectCellColour.backgroundColour) : 'inherit',
                  color: column
                    ? themed.text(multiselectCellColour.fontColour, multiselectCellColour.backgroundColour)
                    : 'inherit',
                  fontWeight: multiselectCellColour.fontBold ? 'bold' : 'normal',
                  fontStyle: multiselectCellColour.fontItalic ? 'italic' : 'normal',
                  textDecoration: multiselectCellColour.fontUnderline ? 'underline' : 'none'
                }}
              >
                {item}
              </span>
            )
          })}
        </span>
      )
      displayStringValue = stringValue.join(', ')
    } else if (column.column.kind === 'json') {
      // Same canonical text the JSON cell itself shows, so a group header reads like the
      // cells beneath it — and matches the key rows were grouped on.
      displayStringValue = previewOf(value as unknown as IJsonCellValue)
      displayValue = displayStringValue
    } else if (column.column.kind === 'link') {
      displayValue = (
        <a href={value as string} target="_blank" rel="noopener noreferrer">
          {value ? column.column.displayLink ? value.toString() : <Link /> : ''}
        </a>
      )
    }
  }

  const handleOnContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    onContext({
      ...INITIAL_CONTEXT_MENU_STATE,
      open: true,
      top: `${event.clientY + 5}px`,
      left: `${event.clientX - 120}px`,
      right: 'auto',
      bottom: 'auto',
      columnId,
      groupKey: groupItemKey,
      rowId: row?.publicId
    })
  }

  const isSticky = column.isFrozen

  return (
    <div
      className={styles.container({
        frozen: isSticky,
        inSelectedColumn,
        showValue: shouldShowValue,
        withinCollapsedGroup: isWithinCollapsedGroup,
        topLine: (shouldShowValue || !!isWithinCollapsedGroup) && !isFirstRow,
        lastFrozen: column.isLastFrozenColumn,
        fontBold: cellColour.fontBold,
        fontItalic: cellColour.fontItalic,
        fontUnderline: cellColour.fontUnderline
      })}
      style={{
        width: `${column.column.width}px`,
        height: heightPx,
        backgroundColor:
          column && column.column && column.column.kind !== 'multiselect'
            ? themed.fill(cellColour.backgroundColour)
            : 'var(--color-surface-sunken)',
        color:
          column && column.column && column.column.kind !== 'multiselect'
            ? themed.text(cellColour.fontColour, cellColour.backgroundColour)
            : 'var(--color-body)',
        ...(isSticky ? { left: `${column.left}px` } : { transform: `translateX(${column.left}px)` })
      }}
      onContextMenu={groupItemKey ? handleOnContextMenu : undefined}
      onMouseDown={handleOnMouseDown}
      data-testid="spreadsheet-group-cell"
      data-columnid={columnId}
      data-isgroupheader={!!(value !== undefined && shouldShowValue)}
      data-selected={selected || undefined}
    >
      {/* draw the outline if selected */}
      {selected && shouldShowValue && (
        <div
          data-testid="group-cell-selection-outline"
          className="absolute left-0 top-0 z-[5] pointer-events-none outline-1 -outline-offset-1 outline-blue"
          style={{ width: `${spanWidth ?? column.column.width}px`, height: `${spanHeightPx}px` }}
        />
      )}
      {value !== undefined && shouldShowValue ? (
        <div className="relative h-full text-ellipsis" title={displayStringValue}>
          {/* The cells' 8px inset, and the first line level with the text of the
              row beside it (a 20px line centred in the row), so the label lines up
              with the grid instead of sitting in the top corner. */}
          <span
            data-copy-text
            data-testid="group-cell-value"
            className="absolute inline-block break-words overflow-hidden select-text px-[8px]"
            style={{
              width: `${maxWidth}px`,
              paddingTop: `${firstLineTop}px`,
              maxHeight: `${spanHeightPx - firstLineTop}px` // Allow text to span multiple rows, minus padding
            }}
          >
            {displayValue}
          </span>
          {/* A box the height of the label's first line, at the same offset, with
              the chevron centred in it: on the text's centre line, where an inline
              icon sat low on the baseline. */}
          <span
            data-testid="group-cell-toggle"
            className="absolute flex items-center cursor-pointer right-[8px] h-5 text-muted hover:text-body transition-colors"
            style={{ top: `${firstLineTop}px` }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={handleToggle}
          >
            <Triangle className="w-2.5 h-2.5" type={canCollapse ? 'down' : 'right'} />
          </span>
        </div>
      ) : null}
    </div>
  )
}

export default GroupCell
